import React, { useEffect, useState } from 'react';
import { Typography, Paper, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, Snackbar, Alert, MenuItem, Select, InputLabel, FormControl, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import api from '../api/axios';

interface TenantOption {
  id: number;
  name: string;
}

interface Platform {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
}

interface PlatformAssignment {
  platform_id: number;
  platform_name: string;
  credential_id?: number;
  credentials?: Record<string, any>;
  tenant_id: number;
  is_active: boolean;
}

const PlatformsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [assignments, setAssignments] = useState<PlatformAssignment[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assigning, setAssigning] = useState<{[key: string]: boolean}>({});
  const [editDialog, setEditDialog] = useState<{ open: boolean; platformId: number | null; tenantId: number | null; username: string; password: string }>({ open: false, platformId: null, tenantId: null, username: '', password: '' });

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenants/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(res.data as TenantOption[]);
    } catch (err: any) {
      setError('Failed to fetch tenants');
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/platforms/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatforms(res.data as Platform[]);
    } catch (err: any) {
      setError('Failed to fetch platforms');
    }
  };

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      let allAssignments: PlatformAssignment[] = [];
      for (const tenant of tenants) {
        const res = await api.get(`/api/v1/admin/tenants/${tenant.id}/platforms/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const tenantAssignments = (res.data as PlatformAssignment[]).map(a => ({ ...a, tenant_id: tenant.id }));
        allAssignments = allAssignments.concat(tenantAssignments);
      }
      setAssignments(allAssignments);
    } catch (err: any) {
      setError('Failed to fetch platform assignments');
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchPlatforms();
  }, []);

  useEffect(() => {
    if (tenants.length > 0) {
      fetchAssignments();
    }
    // eslint-disable-next-line
  }, [tenants]);

  const handleAssign = (platform: Platform, tenantId: number) => {
    setEditDialog({ open: true, platformId: platform.id, tenantId, username: '', password: '' });
  };

  const handleUnassign = async (platform: Platform, tenantId: number) => {
    setAssigning(a => ({ ...a, [`${platform.id}-${tenantId}`]: true }));
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/v1/admin/platforms/${platform.id}/deactivate`, {
        tenant_id: tenantId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(`Platform unassigned from tenant`);
      fetchAssignments();
    } catch (err: any) {
      setError('Failed to unassign platform');
    } finally {
      setAssigning(a => ({ ...a, [`${platform.id}-${tenantId}`]: false }));
    }
  };

  const isAssigned = (platformId: number, tenantId: number) => {
    return assignments.some(a => a.platform_id === platformId && a.tenant_id === tenantId && a.is_active);
  };

  const handleOpenEdit = (platformId: number, tenantId: number) => {
    const assignment = assignments.find(a => a.platform_id === platformId && a.tenant_id === tenantId);
    setEditDialog({
      open: true,
      platformId,
      tenantId,
      username: assignment?.credentials?.username || '',
      password: assignment?.credentials?.password || ''
    });
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, platformId: null, tenantId: null, username: '', password: '' });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.platformId || !editDialog.tenantId) return;
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const creds = { username: editDialog.username, password: editDialog.password };
      await api.post(`/api/v1/admin/tenants/${editDialog.tenantId}/platforms/${editDialog.platformId}/credentials`, creds, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Credentials updated');
      fetchAssignments();
      handleCloseEdit();
    } catch (err: any) {
      setError('Failed to update credentials');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Platforms</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Assignments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {platforms.map((platform) => (
                <TableRow key={platform.id}>
                  <TableCell>{platform.id}</TableCell>
                  <TableCell>{platform.display_name || platform.name}</TableCell>
                  <TableCell>{platform.description}</TableCell>
                  <TableCell>{platform.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {tenants.map((tenant) => {
                        const assigned = isAssigned(platform.id, tenant.id);
                        return assigned ? (
                          <span key={tenant.id} style={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              label={tenant.name}
                              color="success"
                              onDelete={() => handleUnassign(platform, tenant.id)}
                              disabled={assigning[`${platform.id}-${tenant.id}`]}
                            />
                            <IconButton size="small" onClick={() => handleOpenEdit(platform.id, tenant.id)} title="Edit Credentials">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        ) : (
                          <Button
                            key={tenant.id}
                            size="small"
                            variant="outlined"
                            onClick={() => handleAssign(platform, tenant.id)}
                            disabled={assigning[`${platform.id}-${tenant.id}`]}
                          >
                            Assign to {tenant.name}
                          </Button>
                        );
                      })}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}><Alert severity="error">{error}</Alert></Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}><Alert severity="success">{success}</Alert></Snackbar>
        <Dialog open={editDialog.open} onClose={handleCloseEdit} maxWidth="xs" fullWidth>
          <DialogTitle>Platform Credentials</DialogTitle>
          <DialogContent>
            <TextField
              label="Username"
              value={editDialog.username}
              onChange={e => setEditDialog({ ...editDialog, username: e.target.value })}
              fullWidth
              margin="normal"
              autoFocus
            />
            <TextField
              label="Password"
              type="password"
              value={editDialog.password}
              onChange={e => setEditDialog({ ...editDialog, password: e.target.value })}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={!editDialog.username || !editDialog.password}>Save</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default PlatformsPage; 